/**
 * Builds the field list for `WigFilterPanel` from `/metadata` rows.
 *
 * Two kinds of entry come out of here:
 *
 *   - deal columns, named exactly as the column   (`deal_status`)
 *   - child-table columns, named `prefix.column`  (`wells.spud_date`)
 *
 * The prefixes must match the registry de-hub validates at import
 * (`de_api/broker_deals/filters.py`); anything else comes back 422 rather than
 * silently matching nothing.
 *
 * Up to two hops. The tract tables sit under wells / lease deeds and carry
 * county, TRS and acreage — the terms a land team searches in — so they are
 * registered with an intermediate hop on the server. Three hops
 * (`well_tract_regulatory`) stays out; see the de-hub module header.
 */

import { fieldEditorDescriptor, titleCase } from "./metadataV2Columns";

/**
 * Child tables offered in the filter picker, in display order.
 *
 * `name` is the wire prefix (must match the de-hub registry); `label` is what
 * the user sees, both as the picker group heading and on a has-any/has-none
 * chip.
 */
export const DEAL_FILTER_ENTITIES = [
  { name: "wells", label: "Wells" },
  { name: "well_tracts", label: "Well Tracts" },
  { name: "lease_deeds", label: "Lease Deeds" },
  { name: "lease_deed_tracts", label: "Lease Deed Tracts" },
  { name: "deal_entities", label: "Buyers / Sellers" },
  { name: "approvals", label: "Approvals" },
];

const BOOLEAN_VALUES = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

/**
 * Decides how one metadata row should be filtered: which editor the chip
 * shows, and (for pick lists) the values it offers.
 *
 * Classification is delegated to `fieldEditorDescriptor`, the same function
 * the edit forms use, so a column cannot end up with a dropdown in one place
 * and a text box in the other.
 *
 * Three sources of values, in order:
 *   - `select`  — the enum options are inline on the metadata row
 *   - `lookup`  — an FK whose labels live in another table; resolved values
 *                 are passed in via `lookupValues`, keyed by the metadata's
 *                 `distinct_values_lookup`
 *   - `boolean` — Yes / No
 *
 * A lookup with no resolved values falls back to a text box rather than an
 * empty dropdown. An empty dropdown is a dead end; a text box at least lets
 * someone who knows the id get on with it.
 *
 * @param {object} m             metadata row
 * @param {object} lookupValues  { [lookupName]: [{label, value}] }
 */
const filterEditorFor = (m, lookupValues = {}) => {
  const d = fieldEditorDescriptor(m);

  switch (d.kind) {
    case "select": {
      const values = (d.options || [])
        .filter((o) => o && o.value !== undefined && o.value !== null)
        .map((o) => ({ label: o.label ?? String(o.value), value: o.value }));
      return values.length
        ? { inputType: "select", values }
        : { inputType: "text" };
    }
    case "lookup": {
      const values = lookupValues[d.lookup];
      return values && values.length
        ? { inputType: "select", values }
        : { inputType: "text" };
    }
    case "boolean":
      return { inputType: "select", values: BOOLEAN_VALUES };
    case "number":
      return { inputType: "number" };
    case "date":
    case "timestamp":
      return { inputType: "date" };
    default:
      return { inputType: "text" };
  }
};

/**
 * Turns a `useLookupLabels`-style map (`{ lookup: { id: label } }`) into the
 * `{ lookup: [{label, value}] }` the chip editor wants, sorted by label so the
 * dropdown reads alphabetically rather than by insertion order.
 *
 * Numeric-looking ids are converted back to numbers. Object keys are always
 * strings, and while the API coerces a numeric string for an integer column,
 * sending the right type keeps the saved-search payload honest.
 */
export const lookupValuesFromLabels = (labelByLookup = {}) => {
  const out = {};
  Object.entries(labelByLookup).forEach(([lookup, byId]) => {
    if (!byId) return;
    out[lookup] = Object.entries(byId)
      .map(([id, label]) => ({
        label: String(label),
        value: /^-?\d+$/.test(id) ? Number(id) : id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });
  return out;
};

const labelFor = (m) =>
  m.label && m.label !== m.field ? m.label : titleCase(m.field);

/**
 * Converts metadata rows for ONE table into chip fields.
 *
 * @param {Array}  metadata  — UIMetadata rows
 * @param {object} [opts]
 * @param {string} [opts.entity]       — child prefix; omit for deal columns
 * @param {string} [opts.entityLabel]  — human label for the group
 * @param {object} [opts.lookupValues] — resolved FK options by lookup name
 * @returns {Array} WigFilterField[]
 */
export const metadataToFilterFields = (metadata, opts = {}) => {
  const { entity, entityLabel, lookupValues } = opts;
  if (!Array.isArray(metadata) || metadata.length === 0) return [];

  return [...metadata]
    .sort((a, b) => (a.ord ?? 9999) - (b.ord ?? 9999))
    .filter((m) => m && m.field && !m.hide && m.data_type !== "LongText")
    .map((m) => ({
      name: entity ? `${entity}.${m.field}` : m.field,
      label: labelFor(m),
      ...(entity ? { entity, entityLabel } : {}),
      ...filterEditorFor(m, lookupValues),
    }));
};

/**
 * Combines deal metadata with any child metadata that has loaded into one
 * field list.
 *
 * Child metadata is optional on purpose: the deals grid must still filter on
 * deal columns if a child `/metadata` call fails or has not returned yet.
 * A missing child simply means its fields are absent from the picker — the
 * has-any / has-none entries come from `DEAL_FILTER_ENTITIES` and stay
 * available regardless.
 *
 * @param {Array}  dealMetadata
 * @param {object} childMetadata — { wells: [...], lease_deeds: [...] }
 * @param {object} lookupValues  — { brokers_xref: [{label, value}], ... }
 * @returns {Array} WigFilterField[]
 */
export const buildDealFilterFields = (
  dealMetadata,
  childMetadata = {},
  lookupValues = {}
) => {
  const fields = metadataToFilterFields(dealMetadata, { lookupValues });

  // Deal Key is `hide: true` in the metadata, so the loop above drops it — the
  // same reason the grid column is injected by hand in dealsList. Hiding it
  // from the COLUMNS is right (it is already the pinned first column); hiding
  // it from the FILTERS is not: "deal_key is any of <a list pasted out of a
  // spreadsheet>" is the land team's most common search, and with the field
  // absent there was no way to express it once the separate paste box was
  // folded into the chips (feedback #69).
  if (!fields.some((f) => f.name === DEAL_KEY_FIELD)) {
    fields.unshift({
      name: DEAL_KEY_FIELD,
      label: "Deal Key",
      inputType: "text",
    });
  }

  DEAL_FILTER_ENTITIES.forEach(({ name, label }) => {
    const rows = childMetadata[name];
    if (!rows || !rows.length) return;
    fields.push(
      ...metadataToFilterFields(rows, {
        entity: name,
        entityLabel: label,
        lookupValues,
      })
    );
  });

  return fields;
};

/**
 * Converts the chip field list into the `DataItem` shape `WigSearch` (the
 * Advanced query builder) wants.
 *
 * ONE source of truth for both filtering surfaces. Advanced used to be built
 * by `metadataV2ToSearchFields`, a second pass over the same metadata, and the
 * two disagreed in ways the user could see:
 *
 *   - FK columns (Brokerage, Warwick Landman, AOI, Operator) got a dropdown on
 *     the chips and a FREE TEXT box in Advanced, because the ids were never
 *     resolved to names for it;
 *   - child-table fields (`wells.spud_date` and friends) existed on the chips
 *     and were absent from Advanced entirely;
 *   - a date column offered `contains` in Advanced, which means nothing.
 *
 * Feeding Advanced the chips' own field list fixes all three at once, and a
 * search saved from either surface now loads into the other with the same
 * labels and the same options.
 *
 * Two naming traps in `DataItem`, both legacy:
 *   - `key` is the WIRE name; `name` is the LABEL (WigSearch reads its alias
 *     from `name || title || alias`). They are not what they sound like.
 *   - `input_type` drives the operator list. "select" is deliberate over
 *     "singleSelect": it offers is / is not / is any of / is none of, and the
 *     value editor upgrades itself to a multi-select when the operator is
 *     `in` / `nin`.
 *
 * @param {Array} fields  WigFilterField[] from buildDealFilterFields
 * @returns {Array} DataItem[]
 */
export const filterFieldsToSearchFields = (fields = []) =>
  (Array.isArray(fields) ? fields : []).map((f) => {
    const values = Array.isArray(f.values) ? f.values : [];
    const inputType = values.length
      ? "select"
      : f.inputType === "number" || f.inputType === "date"
        ? f.inputType
        : "text";

    return {
      key: f.name,
      // Child fields carry their table, so "Spud Date" cannot be mistaken for
      // a deal column in a flat picker.
      name: f.entityLabel ? `${f.entityLabel} — ${f.label}` : f.label,
      input_type: inputType,
      // Passed through as {label, value} pairs -- which is the whole point:
      // the id goes on the wire, the name goes on screen.
      ...(values.length ? { distinct_values: values } : {}),
    };
  });

/**
 * The deal's natural key. The pasted Deal Key list is expressed as a normal
 * filter rule on this field rather than as separate client-side state.
 */
export const DEAL_KEY_FIELD = "deal_key";

const EMPTY_QUERY = { combinator: "and", rules: [] };

const rulesOf = (query) => (Array.isArray(query?.rules) ? query.rules : []);

const isDealKeyRule = (r) =>
  r && r.field === DEAL_KEY_FIELD && (r.operator === "in" || r.operator === "nin");

/**
 * Reads the pasted key list back out of a query.
 *
 * @returns {Array|null} the keys, or null when no deal-key rule is present.
 *   null and [] mean different things: null is "no key filter", [] would be
 *   "match nothing".
 */
export const dealKeysFromQuery = (query) => {
  const rule = rulesOf(query).find(
    (r) => isDealKeyRule(r) && r.operator === "in"
  );
  if (!rule) return null;
  return Array.isArray(rule.value) ? rule.value : [rule.value];
};

/**
 * Returns a query with the deal-key rule set to `keys`, or removed when the
 * list is empty.
 *
 * Every other rule is preserved and ordering is stable, so applying a key list
 * on top of existing chips reads as "and also these keys" rather than
 * replacing what the user already set up.
 */
export const withDealKeys = (query, keys) => {
  const base = query && Array.isArray(query.rules) ? query : EMPTY_QUERY;
  const others = rulesOf(base).filter((r) => !isDealKeyRule(r));
  const list = (keys || []).map((k) => String(k).trim()).filter(Boolean);

  return {
    combinator: base.combinator === "or" ? "or" : "and",
    rules: list.length
      ? [...others, { field: DEAL_KEY_FIELD, operator: "in", value: list }]
      : others,
  };
};

/**
 * How many conditions a query carries. Drives the "is anything filtered"
 * checks around the grid -- the reset control is inert without one, and a
 * short row list with no stated reason reads as the whole dataset.
 */
export const countActiveFilters = (query) => rulesOf(query).length;

export default buildDealFilterFields;
